import React, { FC, useEffect, useState } from 'react'
import { Tr } from '../../../../_metronic/i18n/Tr'
import { fetchData, fetchGetData, getTranslatedValue } from '../../../../reusable'
import { useLang } from '../../../../_metronic/i18n/Metronici18n'
import { USER_LEVELS } from './AddAdvertisement.constants'

interface TargetingForm {
  gender: string
  region: string[]
  ageMin: string
  ageMax: string
  userLevel: string[]
  interest: string[]
}

// The targeting master API returns the age field as key `age`; `ageRange` is kept for older data
const AGE_KEYS = ['age', 'ageRange', 'age_range']

interface AddAdvertisementTargetingTabProps {
  targetingForm: TargetingForm
  onFieldChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void
  onToggleChange: (name: keyof TargetingForm, value: string) => void
  onMultiToggleChange: (name: keyof TargetingForm, value: string[]) => void
  onPrevious: () => void
  onNext: () => void
}

const AddAdvertisementTargetingTab: FC<AddAdvertisementTargetingTabProps> = ({
  targetingForm,
  onFieldChange,
  onToggleChange,
  onMultiToggleChange,
  onPrevious,
  onNext,
}) => {
  const t = Tr()
  const lang = useLang()
  const [targetList, setTargetList] = useState<any[]>([])
  const [regionList, setRegionList] = useState<any[]>([])
  const [interestList, setInterestList] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [ageError, setAgeError] = useState('')

  const MIN_AGE = 0
  const MAX_AGE = 100

  // Age is optional: leaving both min and max empty means "All Ages" and is valid.
  // If either is filled, both must be whole numbers inside MIN_AGE..MAX_AGE with min < max.
  const validateAgeRange = (minRaw: any, maxRaw: any): string => {
    const min = String(minRaw ?? '').trim()
    const max = String(maxRaw ?? '').trim()

    if (!min && !max) return ''
    if (!min || !max) return t('Please enter both minimum and maximum age')
    if (!/^\d+$/.test(min) || !/^\d+$/.test(max)) return t('Age must be a whole number')

    const from = Number(min)
    const to = Number(max)
    if (from < MIN_AGE || to > MAX_AGE) {
      return `${t('Age must be between')} ${MIN_AGE} ${t('and')} ${MAX_AGE}`
    }
    if (from >= to) return t('Minimum age must be less than maximum age')
    return ''
  }

  const handleAgeChange = (field: 'ageMin' | 'ageMax', value: string) => {
    // Keep only digits so the field can never hold a decimal, sign or exponent
    onToggleChange(field, value.replace(/[^\d]/g, ''))
  }

  const handleNext = () => {
    const error = validateAgeRange(targetingForm.ageMin, targetingForm.ageMax)
    setAgeError(error)
    if (error) return
    onNext()
  }

  // Re-validate on every change, including values pre-populated from saved edit data
  useEffect(() => {
    setAgeError(validateAgeRange(targetingForm.ageMin, targetingForm.ageMax))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetingForm.ageMin, targetingForm.ageMax])

  const ToggleGroup = ({
    label,
    name,
    value,
    options,
  }: {
    label: string
    name: keyof TargetingForm
    value: string
    options: { label: string; value: string }[]
  }) => (
    <div className='mb-6'>
      <label className='form-label fw-semibold text-gray-700 mb-3'>{label}</label>
      <div className='d-flex flex-wrap gap-2'>
        {options.map((opt) => {
          const isActive = value === opt.value
          return (
            <button
              key={opt.value}
              type='button'
              onClick={() => onToggleChange(name, opt.value)}
              style={{
                border: isActive ? '2px solid #009ef7' : '1.5px solid #e4e6ef',
                borderRadius: '8px',
                padding: '8px 20px',
                cursor: 'pointer',
                background: '#fff',
                color: isActive ? '#009ef7' : '#5e6278',
                fontWeight: isActive ? 600 : 400,
                fontSize: '14px',
                transition: 'all 0.15s ease',
                outline: 'none',
                boxShadow: 'none',
              }}
            >
              {opt.label}
            </button>
          )
        })}
      </div>
    </div>
  )

  const getTarget = async () => {
    setLoading(true)
    const res = await fetchGetData('public/getadstargetmasterlist')
    if (String(res?.status) === '200') {
      setTargetList(res?.data || [])
    }
    setLoading(false)
  }

  const getRegion = async () => {
    const res = await fetchData('public/regionList', {})
    if (String(res?.status) === '200') {
      setRegionList(res?.data || [])
    }
  }

  const getInterest = async () => {
    const res = await fetchGetData('public/interest/list')
    if (String(res?.status) === '200') {
      // this API returns the list under `message`
      const list = Array.isArray(res?.data) ? res.data : Array.isArray(res?.message) ? res.message : []
      setInterestList(list)
    }
  }

  useEffect(() => {
    getTarget()
    getRegion()
    getInterest()
  }, [])

  const getInterestId = (row: any) => String(row?.interestId ?? '')
  const getInterestName = (row: any) => getTranslatedValue(lang, row, '_name') || '-'

  const chipStyle = (isActive: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    border: isActive ? '2px solid #009ef7' : '1.5px solid #e4e6ef',
    borderRadius: '8px',
    padding: '8px 20px',
    cursor: 'pointer',
    background: '#fff',
    color: isActive ? '#009ef7' : '#5e6278',
    fontWeight: isActive ? 600 : 400,
    fontSize: '14px',
    transition: 'all 0.15s ease',
    outline: 'none',
    boxShadow: 'none',
  })

  const toggleUserLevel = (level: string) => {
    const selected = targetingForm.userLevel || []
    const next = selected.includes(level)
      ? selected.filter((row) => row !== level)
      : [...selected, level]
    onMultiToggleChange('userLevel', next)
  }

  const toggleRegion = (id: string) => {
    const selected = targetingForm.region || []
    const next = selected.includes(id)
      ? selected.filter((row) => row !== id)
      : [...selected, id]
    onMultiToggleChange('region', next)
  }

  const toggleInterest = (id: string) => {
    const selected = targetingForm.interest || []
    const next = selected.includes(id)
      ? selected.filter((row) => row !== id)
      : [...selected, id]
    onMultiToggleChange('interest', next)
  }

  // The API names the age field `age` with inputType `input`; older data used `ageRange`
  const isAgeField = (item: any) =>
    item?.inputType === 'input' || AGE_KEYS.includes(String(item?.key))

  const renderTargetField = (item: any) => {
    const { key, label, description } = item

    if (isAgeField(item)) return renderAgeField(item)

    switch (key) {
      case 'gender':
        return (
          <div key={key}>
            <ToggleGroup
              label={t(label) || t('Target Gender')}
              name='gender'
              value={targetingForm.gender}
              options={[
                { label: t('All Genders'), value: '' },
                { label: t('Male'), value: 'male' },
                { label: t('Female'), value: 'female' },
              ]}
            />
            {description && (
              <small className='text-muted d-block mt-n4 mb-4'>{t(description)}</small>
            )}
          </div>
        )

      case 'region':
        return renderRegionField(item)

      case 'userLevel':
        return renderUserLevelField(item)

      case 'interest':
        return renderInterestField(item)

      default:
        return null
    }
  }

  const renderAgeField = (item: any) => {
    const { key, label, description } = item
    return (
          <div key={key} className='mb-6'>
            <label className='form-label fw-semibold text-gray-700 mb-3'>
              {t(label) || t('Target Age Range')}
              <span className='text-muted fw-normal fs-8 ms-2'>({t('optional')})</span>
            </label>
            <div className='d-flex align-items-center gap-3 flex-wrap'>
              <div className='d-flex align-items-center gap-2'>
                <span className='text-muted fs-7 fw-semibold'>{t('Min')}</span>
                <input
                  type='number'
                  inputMode='numeric'
                  className={`form-control form-control-sm ${ageError ? 'is-invalid' : ''}`}
                  style={{ width: 90 }}
                  min={MIN_AGE}
                  max={MAX_AGE}
                  step={1}
                  placeholder={t('Any')}
                  value={targetingForm.ageMin ?? ''}
                  onChange={(e) => handleAgeChange('ageMin', e.target.value)}
                />
              </div>
              <span className='text-muted fw-bold'>—</span>
              <div className='d-flex align-items-center gap-2'>
                <span className='text-muted fs-7 fw-semibold'>{t('Max')}</span>
                <input
                  type='number'
                  inputMode='numeric'
                  className={`form-control form-control-sm ${ageError ? 'is-invalid' : ''}`}
                  style={{ width: 90 }}
                  min={MIN_AGE}
                  max={MAX_AGE}
                  step={1}
                  placeholder={t('Any')}
                  value={targetingForm.ageMax ?? ''}
                  onChange={(e) => handleAgeChange('ageMax', e.target.value)}
                />
              </div>
              <span
                className='badge fs-7 fw-semibold'
                style={{ background: '#e8f4fd', color: '#009ef7', padding: '6px 12px' }}
              >
                {targetingForm.ageMin && targetingForm.ageMax
                  ? `${targetingForm.ageMin} – ${targetingForm.ageMax} ${t('years')}`
                  : t('All Ages')}
              </span>
            </div>
            {ageError && <div className='text-danger fs-7 mt-2'>{ageError}</div>}
            {description && (
              <small className='text-muted d-block mt-2'>{t(description)}</small>
            )}
          </div>
        )

  }

  const renderRegionField = (item: any) => {
    const { key, label, description } = item
    const selected = targetingForm.region || []
    return (
      <div key={key} className='mb-6'>
        <label className='form-label fw-semibold text-gray-700 mb-3'>
          {t(label) || t('Target Region')}
        </label>
        {regionList.length === 0 ? (
          <div className='text-muted fs-7'>{t('No regions available')}</div>
        ) : (
          <div className='d-flex flex-wrap gap-2'>
            {/* "All Regions" clears the selection (empty = every region) */}
            <button
              type='button'
              onClick={() => onMultiToggleChange('region', [])}
              style={chipStyle(selected.length === 0)}
            >
              {t('All Regions')}
            </button>
            {regionList.map((row: any) => {
              const id = String(row?.regionId ?? '')
              const isActive = selected.includes(id)
              return (
                <button
                  key={id}
                  type='button'
                  onClick={() => toggleRegion(id)}
                  style={chipStyle(isActive)}
                >
                  {getTranslatedValue(lang, row, '_name') || '-'}
                </button>
              )
            })}
          </div>
        )}
        {selected.length > 0 && (
          <small className='text-primary d-block mt-2 fw-semibold'>
            {selected.length} {t('selected')}
          </small>
        )}
        {description && (
          <small className='text-muted d-block mt-2'>{t(description)}</small>
        )}
      </div>
    )
  }

  const renderUserLevelField = (item: any) => {
    const { key, label, description } = item
    const selected = targetingForm.userLevel || []
    return (
      <div key={key} className='mb-6'>
        <label className='form-label fw-semibold text-gray-700 mb-3'>
          {t(label) || t('Target User Level')}
        </label>
        <div className='d-flex flex-wrap gap-2'>
          {/* "All Levels" clears the selection (empty = every level) */}
          <button
            type='button'
            onClick={() => onMultiToggleChange('userLevel', [])}
            style={chipStyle(selected.length === 0)}
          >
            {t('All Levels')}
          </button>
          {USER_LEVELS.map((level) => {
            const isActive = selected.includes(level)
            return (
              <button
                key={level}
                type='button'
                onClick={() => toggleUserLevel(level)}
                style={chipStyle(isActive)}
              >
                {t(level.charAt(0).toUpperCase() + level.slice(1))}
              </button>
            )
          })}
        </div>
        {selected.length > 0 && (
          <small className='text-primary d-block mt-2 fw-semibold'>
            {selected.length} {t('selected')}
          </small>
        )}
        {description && (
          <small className='text-muted d-block mt-2'>{t(description)}</small>
        )}
      </div>
    )
  }

  const renderInterestField = (item: any) => {
    const { key, label, description } = item
    return (
          <div key={key} className='mb-6'>
            <label className='form-label fw-semibold text-gray-700 mb-3'>
              {t(label) || t('Interest')}
            </label>
            {interestList.length === 0 ? (
              <div className='text-muted fs-7'>{t('No interests available')}</div>
            ) : (
              <div className='d-flex flex-wrap gap-2'>
                {interestList.map((row: any) => {
                  const id = getInterestId(row)
                  const isActive = (targetingForm.interest || []).includes(id)
                  return (
                    <button
                      key={id}
                      type='button'
                      onClick={() => toggleInterest(id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        border: isActive ? '2px solid #009ef7' : '1.5px solid #e4e6ef',
                        borderRadius: '8px',
                        padding: '8px 20px',
                        cursor: 'pointer',
                        background: '#fff',
                        color: isActive ? '#009ef7' : '#5e6278',
                        fontWeight: isActive ? 600 : 400,
                        fontSize: '14px',
                        transition: 'all 0.15s ease',
                        outline: 'none',
                        boxShadow: 'none',
                      }}
                    >
                      {/* {row?.image && (
                        <img
                          src={row.image}
                          alt=''
                          style={{ width: '18px', height: '18px', objectFit: 'contain' }}
                          onError={(e) => {
                            ;(e.currentTarget as HTMLImageElement).style.display = 'none'
                          }}
                        />
                      )} */}
                      {getInterestName(row)}
                    </button>
                  )
                })}
              </div>
            )}
            {description && (
              <small className='text-muted d-block mt-2'>{t(description)}</small>
            )}
          </div>
        )
  }

  // Build summary rows only for keys present in the API response
  const summaryKeyMap: Record<string, { label: string; getValue: () => string }> = {
    region: {
      label: t('Region'),
      getValue: () => {
        const selected = targetingForm.region || []
        if (selected.length === 0) return t('All Regions')
        return selected
          .map((id) => {
            const row = regionList.find((r: any) => String(r?.regionId) === String(id))
            return row ? getTranslatedValue(lang, row, '_name') || String(id) : String(id)
          })
          .join(', ')
      },
    },
    gender: {
      label: t('Gender'),
      getValue: () =>
        targetingForm.gender
          ? targetingForm.gender.charAt(0).toUpperCase() + targetingForm.gender.slice(1)
          : t('All Genders'),
    },
    age: {
      label: t('Age Range'),
      getValue: () =>
        targetingForm.ageMin && targetingForm.ageMax
          ? `${targetingForm.ageMin} – ${targetingForm.ageMax}`
          : t('All Ages'),
    },
    userLevel: {
      label: t('User Level'),
      getValue: () => {
        const selected = targetingForm.userLevel || []
        if (!selected.length) return t('All Levels')
        return selected
          .map((level) => t(level.charAt(0).toUpperCase() + level.slice(1)))
          .join(', ')
      },
    },
    interest: {
      label: t('Interest'),
      getValue: () => {
        const selected = targetingForm.interest || []
        if (!selected.length) return t('All Interests')
        return selected
          .map((id) => {
            const row = interestList.find((r: any) => getInterestId(r) === id)
            return row ? getInterestName(row) : id
          })
          .join(', ')
      },
    },
  }

  // Age can arrive under any of the AGE_KEYS aliases — map them all onto the `age` summary row
  const summaryKeyFor = (item: any) => (isAgeField(item) ? 'age' : item.key)

  const activeSummaryItems = targetList
    .filter((item) => item.isActive && summaryKeyMap[summaryKeyFor(item)])
    .map((item) => ({ key: item.key, ...summaryKeyMap[summaryKeyFor(item)] }))

  return (
    <>
      <div className='card card-flush py-4'>
        <div className='card-header d-flex align-items-center'>
          <div className='card-title d-flex align-items-center gap-3'>
            <span
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: '#e8f4fd',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
                <circle cx='12' cy='12' r='10' stroke='#009ef7' strokeWidth='1.8' />
                <circle cx='12' cy='12' r='3' stroke='#009ef7' strokeWidth='1.8' />
                <line x1='12' y1='2' x2='12' y2='6' stroke='#009ef7' strokeWidth='1.8' strokeLinecap='round' />
                <line x1='12' y1='18' x2='12' y2='22' stroke='#009ef7' strokeWidth='1.8' strokeLinecap='round' />
                <line x1='2' y1='12' x2='6' y2='12' stroke='#009ef7' strokeWidth='1.8' strokeLinecap='round' />
                <line x1='18' y1='12' x2='22' y2='12' stroke='#009ef7' strokeWidth='1.8' strokeLinecap='round' />
              </svg>
            </span>
            <div>
              <h2 className='mb-0'>{t('Targeting Settings')}</h2>
              <p className='text-muted fs-7 mb-0'>
                {t('Define your target audience for maximum effectiveness')}
              </p>
            </div>
          </div>
        </div>

        <div className='card-body pt-5'>
          {loading ? (
            <div className='d-flex justify-content-center align-items-center py-10'>
              <span className='spinner-border spinner-border-sm text-primary me-2' />
              <span className='text-muted'>{t('Loading targeting options...')}</span>
            </div>
          ) : targetList.length === 0 ? (
            <div
              className='d-flex flex-column align-items-center justify-content-center py-12 text-center'
              style={{
                border: '1.5px dashed #e4e6ef',
                borderRadius: '10px',
                background: '#fafafa',
              }}
            >
              <svg width='48' height='48' viewBox='0 0 24 24' fill='none' className='mb-4'>
                <circle cx='12' cy='12' r='10' stroke='#b5b5c3' strokeWidth='1.5' />
                <circle cx='12' cy='12' r='3' stroke='#b5b5c3' strokeWidth='1.5' />
                <line x1='12' y1='2' x2='12' y2='6' stroke='#b5b5c3' strokeWidth='1.5' strokeLinecap='round' />
                <line x1='12' y1='18' x2='12' y2='22' stroke='#b5b5c3' strokeWidth='1.5' strokeLinecap='round' />
                <line x1='2' y1='12' x2='6' y2='12' stroke='#b5b5c3' strokeWidth='1.5' strokeLinecap='round' />
                <line x1='18' y1='12' x2='22' y2='12' stroke='#b5b5c3' strokeWidth='1.5' strokeLinecap='round' />
              </svg>
              <p className='fw-semibold text-gray-600 mb-1'>{t('No Targeting Options Available')}</p>
              <p className='text-muted fs-7 mb-0'>
                {t('No targeting fields have been configured. Your ad will be shown to all users.')}
              </p>
            </div>
          ) : (
            <>
              {targetList
                .filter((item) => item.isActive)
                .map((item) => renderTargetField(item))}

              {activeSummaryItems.length > 0 && (
                <div
                  style={{
                    border: '1.5px solid #e4e6ef',
                    borderRadius: '10px',
                    padding: '16px 20px',
                    background: '#fafafa',
                    marginBottom: '16px',
                  }}
                >
                  <div className='fw-bold text-gray-800 mb-3'>{t('Current Targeting Summary')}</div>
                  <div className='row g-2'>
                    {activeSummaryItems.map((item) => (
                      <div key={item.key} className='col-md-6'>
                        <span className='text-muted fs-7'>{item.label}: </span>
                        <span className='fw-semibold text-gray-800'>{item.getValue()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div
                style={{
                  background: '#e8f4fd',
                  border: '1px solid #b8dff7',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  fontSize: '13px',
                  color: '#374151',
                }}
              >
                <span className='fw-bold'>{t('Note:')} </span>
                {t(
                  'If no specific targeting is selected (all options set to "All"), your ad will be shown to all users visiting the selected placement.'
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <div className='d-flex justify-content-end mt-5 gap-3'>
        <button type='button' className='btn btn-secondary' onClick={onPrevious}>
          {t('Previous')}
        </button>
        <button
          type='button'
          className='btn btn-primary'
          onClick={handleNext}
          disabled={!!ageError}
        >
          {t('Next')}
        </button>
      </div>
    </>
  )
}

export default AddAdvertisementTargetingTab
